import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PrepHeader } from './components/PrepHeader';
import { Home } from './pages/Home';
import { LandingPage } from './pages/LandingPage';
import { About } from './pages/About';
import { Services } from './pages/Services';
import { Contact } from './pages/Contact';
import { FAQ } from './pages/FAQ';
import { Policy } from './pages/Policy';
import { Partners } from './pages/Partners';
import { PrepLanding } from './pages/prep/PrepLanding';
import { PrepPrograms } from './pages/prep/PrepPrograms';
import { PrepLogin } from './pages/prep/PrepLogin';
import { PrepDashboard } from './pages/prep/PrepDashboard';
import { PrepAuthCallback } from './pages/prep/PrepAuthCallback';
import { PrepIndividual } from './pages/prep/PrepIndividual';
import { PrepPaymentSuccess } from './pages/prep/PrepPaymentSuccess';
import { PrepPaymentCancel } from './pages/prep/PrepPaymentCancel';
import { PrepAdminDashboard } from './pages/prep/admin/PrepAdminDashboard';
import { PrepAdminPrograms } from './pages/prep/admin/PrepAdminPrograms';
import { PrepAdminMaterials } from './pages/prep/admin/PrepAdminMaterials';
import { PrepAdminStudents } from './pages/prep/admin/PrepAdminStudents';
import { PrepAdminEnrollments } from './pages/prep/admin/PrepAdminEnrollments';
import { PrepAdminHomeContent } from './pages/prep/admin/PrepAdminHomeContent';
import { PrepAdminCertContent } from './pages/prep/admin/PrepAdminCertContent';
import { PrepAdminIndividualContent } from './pages/prep/admin/PrepAdminIndividualContent';
import { AdminLogin } from './pages/admin/AdminLogin';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminClasses } from './pages/admin/AdminClasses';
import { AdminMessages } from './pages/admin/AdminMessages';
import { AdminFAQs } from './pages/admin/AdminFAQs';
import { AdminTestimonials } from './pages/admin/AdminTestimonials';
import { AdminPartners } from './pages/admin/AdminPartners';
import { AdminPolicies } from './pages/admin/AdminPolicies';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminHomeContent } from './pages/admin/AdminHomeContent';
import { AdminAboutContent } from './pages/admin/AdminAboutContent';
import { AdminContactInfo } from './pages/admin/AdminContactInfo';
import { AdminServicesGroup } from './pages/admin/AdminServicesGroup';
import { Toaster } from './components/ui/sonner';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { PrepAuthProvider, usePrepAuth } from './contexts/PrepAuthContext';

// Protected Route for CPR Admin
const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated } = useAdminAuth();
  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

// Protected Route for Training Prep users
const ProtectedPrepRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePrepAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/prep/login" />;
};

// Protected Route for Training Prep admin
const ProtectedPrepAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = usePrepAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/prep/login" />;
  if (user?.role !== 'admin') return <Navigate to="/prep/dashboard" />;
  return children;
};

// Layout wrappers
const MainLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

const PrepLayout = ({ children }) => (
  <>
    <PrepHeader />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <div className="App">
      <AdminAuthProvider>
        <PrepAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Initial Landing Page */}
              <Route path="/" element={<LandingPage />} />

              {/* Main PUSH Training Site */}
              <Route path="/home" element={<MainLayout><Home /></MainLayout>} />
              <Route path="/about" element={<MainLayout><About /></MainLayout>} />
              <Route path="/services" element={<MainLayout><Services /></MainLayout>} />
              <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
              <Route path="/faq" element={<MainLayout><FAQ /></MainLayout>} />
              <Route path="/policy" element={<MainLayout><Policy /></MainLayout>} />
              <Route path="/partners" element={<MainLayout><Partners /></MainLayout>} />

              {/* PUSH Training Prep Site */}
              <Route path="/prep" element={<PrepLayout><PrepLanding /></PrepLayout>} />
              <Route path="/prep/programs" element={<PrepLayout><PrepPrograms /></PrepLayout>} />
              <Route path="/prep/login" element={<PrepLogin />} />
              <Route path="/prep/auth/callback" element={<PrepAuthCallback />} />
              <Route path="/prep/payment/success" element={<PrepPaymentSuccess />} />
              <Route path="/prep/payment/cancel" element={<PrepPaymentCancel />} />
              <Route path="/prep/individual" element={<PrepLayout><PrepIndividual /></PrepLayout>} />
              <Route path="/prep/dashboard" element={<ProtectedPrepRoute><PrepLayout><PrepDashboard /></PrepLayout></ProtectedPrepRoute>} />

              {/* Training Prep Admin Routes */}
              <Route path="/prep/admin" element={<ProtectedPrepAdminRoute><PrepAdminDashboard /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/programs" element={<ProtectedPrepAdminRoute><PrepAdminPrograms /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/materials" element={<ProtectedPrepAdminRoute><PrepAdminMaterials /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/students" element={<ProtectedPrepAdminRoute><PrepAdminStudents /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/enrollments" element={<ProtectedPrepAdminRoute><PrepAdminEnrollments /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/content/home" element={<ProtectedPrepAdminRoute><PrepAdminHomeContent /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/content/certification" element={<ProtectedPrepAdminRoute><PrepAdminCertContent /></ProtectedPrepAdminRoute>} />
              <Route path="/prep/admin/content/individual" element={<ProtectedPrepAdminRoute><PrepAdminIndividualContent /></ProtectedPrepAdminRoute>} />

              {/* CPR Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
              <Route path="/admin/classes" element={<ProtectedAdminRoute><AdminClasses /></ProtectedAdminRoute>} />
              <Route path="/admin/messages" element={<ProtectedAdminRoute><AdminMessages /></ProtectedAdminRoute>} />
              <Route path="/admin/faqs" element={<ProtectedAdminRoute><AdminFAQs /></ProtectedAdminRoute>} />
              <Route path="/admin/testimonials" element={<ProtectedAdminRoute><AdminTestimonials /></ProtectedAdminRoute>} />
              <Route path="/admin/partners" element={<ProtectedAdminRoute><AdminPartners /></ProtectedAdminRoute>} />
              <Route path="/admin/policies" element={<ProtectedAdminRoute><AdminPolicies /></ProtectedAdminRoute>} />
              <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
              <Route path="/admin/content/home" element={<ProtectedAdminRoute><AdminHomeContent /></ProtectedAdminRoute>} />
              <Route path="/admin/content/about" element={<ProtectedAdminRoute><AdminAboutContent /></ProtectedAdminRoute>} />
              <Route path="/admin/content/contact" element={<ProtectedAdminRoute><AdminContactInfo /></ProtectedAdminRoute>} />
              <Route path="/admin/content/services-group" element={<ProtectedAdminRoute><AdminServicesGroup /></ProtectedAdminRoute>} />
            </Routes>
            <Toaster />
          </BrowserRouter>
        </PrepAuthProvider>
      </AdminAuthProvider>
    </div>
  );
}

export default App;
